import * as React from "react";
import ResultsPanel, {CommonResultsPanelProps, ItemsRenderer} from "../results_panel";
import ChecklistPopupRenderer from "../../checklist_popup_renderer";
import ListItemPopupRenderer from "../../list_item_popup_renderer";
import PopupItemText from "../popup_item_text";
import GroupSeparator from "../group_separator";
import clsx from "clsx";

import type LocationsModel from "./locations_model"

type ActionCallback =
    (list_id: number, task_id: number, filterText: string, element: HTMLElement, e?: Event) => void;
type ItemCoordinatesType = {
    list_id: number|null,
    task_id: number|null,
}

interface ListOfLocationsProps extends CommonResultsPanelProps {

    model: typeof LocationsModel;

    /**
     * Index of item which should be selected
     */
    selectedIndex: number;

    /**
     * The id of the list item which should be marked as "cut" in the interface
     */
    cutItemId?: number;

    /**
     * Action callback on pressing "Enter" on an item
     * @param list_id related list_id or null for task actions
     * @param task_id related task_id or null
     * @param filterText text in the filter
     * @param element DOM element, can be null for newly created list
     * @param e Event
     */
    actionCallback: ActionCallback;
}

const fetchListAndTaskFromElement: (el: HTMLElement) => ItemCoordinatesType = function(el) {
    const li = el.closest("li.searchEverywhere__li");
    if (li) {
        const elementId = li.id || "";
        if (elementId.startsWith("popup_checklist_")) {
            return {list_id: parseInt(elementId.substring("popup_checklist_".length)), task_id: null};
        }
        else if (elementId.startsWith("task_se_")) {
            let task_id = parseInt(elementId.substring("task_se_".length));
            const list_id = maxkir.Task.checklist_id(task_id);
            return {list_id: list_id, task_id: task_id};
        }
    }

    return {
        list_id: null,
        task_id: null,
    }
}

// Component to render list of actions
export default (props: ListOfLocationsProps) => {
    // maxkir.warn("items rerendered: ", props.items)

    const doOpenItem = (elementId: string, e: Event) => {
        const el = document.getElementById(elementId);
        if (el) {
            const createListAction = elementId === 'createListAction';
            props.onBeforeItemOpen(!createListAction);

            if (createListAction) {
                const button = el.querySelector(".newbut") as HTMLButtonElement;
                button.click();
                return;
            }

            if (!maxkir.is_testing()) {
                if (props.actionCallback) {
                    const itemCoords = fetchListAndTaskFromElement(el);
                    if (itemCoords.task_id) {
                        props.actionCallback(null,
                            itemCoords.task_id,
                            props.model.getFilterTerms().join(" "), el, e)
                    }
                    else if (itemCoords.list_id) {
                        props.actionCallback(itemCoords.list_id,
                            null, props.model.getFilterTerms().join(" "), el, e)
                    }
                    else {
                        maxkir.click_link(el, false);
                    }
                }
            }
        }
    }

    const cssClass = clsx("locationsList",
        props.model.onTopLevel() ? "locationsList__withCreateList" : null);

    return <ResultsPanel
        cssClass={cssClass}
        itemsRenderer={new LocationsRenderer(props)}
        doOpenItem={doOpenItem}
        {...props}
    />
}


class LocationsRenderer implements ItemsRenderer {
    _result: JSX.Element[];
    _headersCount: number;
    private _selectedElementId: string;

    private _scopeListId: number;
    private _scopeTaskId: number;

    constructor(props: ListOfLocationsProps) {

        const selectedIndex = props.selectedIndex;
        const filterTerms = props.model.getFilterTerms();
        const cutItemId = props.cutItemId;

        const fwd = (e: Event) => {
            const itemCoords = fetchListAndTaskFromElement(e.target as HTMLElement)
            props.model.setSelectionInfo(itemCoords.list_id, itemCoords.task_id, selectedIndex);
            props.model.forwardState();
            e.preventDefault();
            e.stopPropagation();
        }

        this._result = [];
        this._headersCount = 0;
        const highlighter = (text) => {
            return <PopupItemText text={text} filterTerms={filterTerms}/>
        }

        let hasLists = false;
        props.model.getFoundLists().forEach((item: ListData, index) => {
            if (!hasLists) {
                hasLists = true;
                this._result.push(<GroupSeparator text={I18N('js_lists')}/>);
                this._headersCount++;
            }

            const clz = this._clz(selectedIndex);

            this._result.push(
                <ChecklistPopupRenderer listData={item} extraLiClass={clz}
                                        highlighter={highlighter}
                                        forwardAction={fwd}
                                        forwardRunning={props.model.isLoadingItem(item.id, 0)}/>
            );

            if (clz.indexOf('selected') >= 0) {
                this._selectedElementId = "popup_checklist_" + item.id;
                this._scopeListId = item.id;
            }
        });

        let hasThisListItems = false;
        let hasOtherListItems = false;
        props.model.getFoundListItems().forEach((item: TaskData, index) => {
            if (!hasThisListItems && maxkir.checklist_id === item.checklist_id) {
                hasThisListItems = true;
                this._result.push(<GroupSeparator text={I18N('js_this_list_items')} withBack={!!props.model.getHeader()}/>);
                this._headersCount++;
            }
            if (!hasOtherListItems && maxkir.checklist_id !== item.checklist_id) {
                hasOtherListItems = true;
                const header = props.model.getHeader() || I18N('js_other_list_items');
                this._result.push(<GroupSeparator text={header} withBack={!!props.model.getHeader()}/>);
                this._headersCount++;
            }

            let clz = this._clz(selectedIndex)
            if (cutItemId === item.id) {
                clz = clsx(clz, "itemWasCut")
            }

            this._result.push(
                <ListItemPopupRenderer dom_id={'se_' + item.id} taskData={item} extraLiClass={clz}
                                       filterTerms={filterTerms} forwardAction={fwd}
                                       forwardRunning={props.model.isLoadingItem(item.checklist_id, item.id)}/>
            );

            if (clz.indexOf('selected') >= 0) {
                this._selectedElementId = "task_se_" + item.id;
                this._scopeTaskId = item.id;
                this._scopeListId = maxkir.Task.checklist_id(item.id);
            }
        });

        if (this._result.length === 0) {
            this._result.push(<li className='searchEverywhere__nothingFound'>
                <i className="far fa-empty-set"></i>
                {I18N("js_nothing_found")}
            </li>);
        }

        const clz = this._clz(selectedIndex);
        if (props.model.onTopLevel()) {
            if (clz.indexOf('selected') >= 0) {
                this._selectedElementId = "createListAction";
            }

            this._result.push(<CreateListAction extraLiClass={clz} filterText={filterTerms.join(" ")}
                                                actionCallback={props.actionCallback}/>)
        }

        props.model.setSelectionInfo(this._scopeListId, this._scopeTaskId, selectedIndex);
    }

    _clz(selectedIndex: number): string {
        return clsx('searchEverywhere__li',
            this._result.length - this._headersCount === selectedIndex ? 'selected' : null);
    }

    getSelectedElementId(): string {
        return this._selectedElementId;
    }

    getVisibleCount(): number {
        return this._result.length - this._headersCount;
    }

    renderItems(): JSX.Element[] {
        return this._result;
    }
}

function CreateListAction({extraLiClass, filterText, actionCallback}:
                              {extraLiClass: string, filterText: string, actionCallback: ActionCallback}) {
    const clazz = clsx(extraLiClass, 'searchEverywhere__createList');
    const onClick = (e) => {
        maxkir.add_checklist.run_add(filterText).then(function(json) {
            if (actionCallback) {
                actionCallback(json.id, null, filterText, null, e);
            }
        }, () => {});
        e.preventDefault();
        e.stopPropagation();
    }
    return <li id='createListAction' className={clazz}>
        <button onClick={onClick} className="newbut">{ I18N("js_cmd.add_new_list.text") }</button>
    </li>;
}
